import React from 'react';

const PrivacyPolicyContent = () => {
  return (
    <>
      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">1. Introduction</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Welcome to AirBrief ("we", "our", or "us"). We are committed to protecting and respecting your privacy. 
          This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit 
          our website at <strong>www.air-brief.com</strong> and use our ATPL, DLR, and psychometric assessment 
          preparation services (collectively, the "Service").
        </p>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          By using the Service, you agree to the collection and use of information in accordance with this policy.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">2. Information We Collect</h2>
        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">2.1 Personal Information</h3>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          When you register for an account, we may collect the following personal information:
        </p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Full name (first and last name)</li>
          <li>Email address</li>
          <li>Region/location</li>
          <li>Flight school affiliation (if provided)</li>
          <li>Phone number (if provided)</li>
        </ul>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">2.2 Account & Authentication Data</h3>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Encrypted password (for email-based registration)</li>
          <li>Google account profile information (if using Google Sign-In)</li>
          <li>Device identifiers for session management</li>
        </ul>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">2.3 Usage & Performance Data</h3>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Exam attempt results, scores, and progress</li>
          <li>Game performance data (SKT, MIC, MEK, RMS, ROT, OWT, PPT)</li>
          <li>Course enrollment and subject progress</li>
          <li>Question markings and notes</li>
          <li>Login times and session activity</li>
        </ul>

        <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mt-4 mb-2">2.4 Payment Information</h3>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We collect records of your purchases (subscription plans, courses, game add-ons) including amounts, 
          dates, and payment methods. Actual payment card details are processed securely by our third-party 
          payment processor and are <strong>never stored on our servers</strong>.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">3. How We Use Your Information</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">We use the information we collect to:</p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Create and manage your account</li>
          <li>Provide, personalize, and improve our study and exam preparation services</li>
          <li>Track your learning progress and generate performance analytics</li>
          <li>Process transactions and manage subscriptions</li>
          <li>Send you important updates about your account, subscriptions, or the Service</li>
          <li>Respond to your support tickets and inquiries</li>
          <li>Prevent fraud and enforce our terms of service</li>
          <li>Manage device limits and active sessions</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4. Data Sharing & Disclosure</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We do <strong>not</strong> sell, trade, or rent your personal information to third parties. 
          We may share your information only in the following circumstances:
        </p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li><strong>Service Providers:</strong> Third-party services that help us operate (e.g., payment processors, hosting providers)</li>
          <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process</li>
          <li><strong>Safety:</strong> To protect the rights, property, or safety of AirBrief, our users, or the public</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5. Data Security</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We implement appropriate technical and organizational security measures to protect your personal 
          data against unauthorized access, alteration, disclosure, or destruction. These measures include:
        </p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Encryption of passwords using industry-standard hashing algorithms</li>
          <li>Secure HTTPS connections for all data transmission</li>
          <li>JWT-based authentication with token expiration</li>
          <li>Device-based session management with limits</li>
        </ul>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-2">
          However, no method of transmission or electronic storage is 100% secure, and we cannot 
          guarantee absolute security.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">6. Data Retention</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We retain your personal information for as long as your account is active or as needed to provide 
          you with our services. If you request account deletion, we will permanently delete your account 
          and all associated data, including exam results, game scores, and purchase history.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">7. Your Rights</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">You have the right to:</p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li><strong>Access</strong> your personal data through your profile settings</li>
          <li><strong>Update or correct</strong> your personal information at any time</li>
          <li><strong>Delete</strong> your account and all associated data</li>
          <li><strong>Object</strong> to the processing of your personal data</li>
          <li><strong>Request</strong> a copy of the data we hold about you</li>
        </ul>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-2">
          To exercise any of these rights, please contact us through the support ticket system 
          or email us directly.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">8. Cookies & Local Storage</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We use browser local storage to maintain your authentication tokens and user preferences 
          (such as theme settings). We do not use third-party tracking cookies for advertising purposes.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">9. Third-Party Services</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Our Service integrates with the following third-party services:
        </p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li><strong>Google Sign-In:</strong> For authentication. Subject to Google's Privacy Policy.</li>
          <li><strong>Payment Processors:</strong> For secure payment handling. Your payment data is handled according to their respective privacy policies.</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">10. Children's Privacy</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          Our Service is not directed to individuals under the age of 16. We do not knowingly collect 
          personal information from children. If we become aware that we have collected personal data 
          from a child under 16, we will take steps to delete that information.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">11. Changes to This Policy</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          We may update this Privacy Policy from time to time. We will notify you of any changes by 
          posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued 
          use of the Service after any changes constitutes your acceptance of the updated policy.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">12. Contact Us</h2>
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
          If you have any questions about this Privacy Policy or our data practices, please contact us:
        </p>
        <ul className="list-disc pl-6 text-gray-700 dark:text-gray-300 space-y-1">
          <li>Via the Support section in your account</li>
          <li>Email: <strong>support@air-brief.com</strong></li>
        </ul>
      </section>
    </>
  );
};

export default PrivacyPolicyContent;
